import apiClient from '../../services/api';
import type { OrderItem, OrderStatus, PaymentStatus } from '../../shop/shopApi';
import type { Paginated } from '../../types';

export interface AdminProduct {
  id: string;
  name: string;
  category: string;
  pack_label: string;
  price: string;
  description: string | null;
  photo: string | null;
  active: boolean;
  in_stock: boolean;
  featured: boolean;
  max_per_order: number;
  sort_order: number;
}

export interface AdminEvent { label: string; note: string | null; by_customer: boolean; by_user_id: string | null; public: boolean; created_at: string }

export interface AdminOrder {
  id: string;
  ref: string;
  status: OrderStatus;
  payment_status: PaymentStatus;
  payment_method: 'UPI' | 'Cash';
  customer_name: string;
  mobile: string;
  flat: string;
  building: string | null;
  area: string;
  landmark: string | null;
  pincode: string;
  notes: string | null;
  delivery_date: string;
  delivery_slot: string;
  subtotal: string;
  delivery_fee: string;
  total: string;
  upi_id: string | null;
  utr: string | null;
  paid_claimed_at: string | null;
  verified_at: string | null;
  cancel_reason: string | null;
  customer_id: string | null;
  created_at: string;
  items: OrderItem[];
  events: AdminEvent[];
}

export interface ShopSettings {
  accepting_orders: boolean;
  upi_id: string;
  payee_name: string;
  cash_on_delivery: boolean;
  delivery_fee: string | number;
  free_delivery_above: string | number;
  min_order: string | number;
  pincodes: string[];
  delivery_area_note: string;
  slots: ('Morning' | 'Evening')[];
  days_ahead: number;
  same_day_until: number | null;
  notice: string;
  paused_message: string;
  support_phone: string;
  order_note: string;
  signups_open: boolean;
  signup_billing: 'choose' | 'Prepaid' | 'Postpaid';
}

export interface ShopSummary { to_verify: number; to_confirm: number; today: number; refund_due: number; awaiting_payment: number }

export interface AccountPayment {
  id: string; ref: string; amount: string; status: PaymentStatus; utr: string | null; upi_id: string | null;
  created_at: string; verified_at: string | null; note: string | null;
  customer_id: string; customer_name: string; customer_code: string | null; mobile: string | null;
}
export interface CustomerLogin { id: string; customer_code: string | null; name: string; mobile: string | null; party_type: string; subscription_active: boolean; has_pin: boolean; signed_up_online_at: string | null }

export const shopAdminApi = {
  accountPayments: (view: string, page: number, page_size: number) =>
    apiClient.get<Paginated<AccountPayment>>('/shop/account-payments', { params: { view: view || undefined, page, page_size } }).then((r) => r.data),
  accountPaymentAction: (id: string, action: 'verify' | 'not_received') => apiClient.post<AccountPayment>(`/shop/account-payments/${id}`, { action }).then((r) => r.data),
  customerLogins: (q: string) => apiClient.get<CustomerLogin[]>('/shop/customer-logins', { params: { q: q || undefined } }).then((r) => r.data),
  newPin: (id: string) => apiClient.post<{ pin: string; mobile: string }>(`/shop/customer-logins/${id}/new-pin`).then((r) => r.data),
  summary: () => apiClient.get<ShopSummary>('/shop/summary').then((r) => r.data),
  orders: (params: { view?: string; q?: string; delivery_date?: string; page: number; page_size: number }) =>
    apiClient.get<Paginated<AdminOrder>>('/shop/orders', { params }).then((r) => r.data),
  payment: (id: string, action: 'verify' | 'not_received' | 'cash_received' | 'refunded', note?: string) =>
    apiClient.post<AdminOrder>(`/shop/orders/${id}/payment`, { action, note: note || undefined }).then((r) => r.data),
  status: (id: string, status: 'Confirmed' | 'Out for delivery' | 'Delivered' | 'Cancelled', note?: string) =>
    apiClient.post<AdminOrder>(`/shop/orders/${id}/status`, { status, note: note || undefined }).then((r) => r.data),
  note: (id: string, note: string, isPublic: boolean) => apiClient.post<AdminOrder>(`/shop/orders/${id}/note`, { note, public: isPublic }).then((r) => r.data),
  products: () => apiClient.get<AdminProduct[]>('/shop/products').then((r) => r.data),
  saveProduct: (p: Partial<AdminProduct>) => (p.id
    ? apiClient.put<AdminProduct>(`/shop/products/${p.id}`, p)
    : apiClient.post<AdminProduct>('/shop/products', p)).then((r) => r.data),
  deleteProduct: (id: string) => apiClient.delete(`/shop/products/${id}`),
  uploadPhoto: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<{ name: string }>('/shop/products/photo', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
    return data.name;
  },
  settings: () => apiClient.get<ShopSettings>('/shop/settings').then((r) => r.data),
  saveSettings: (s: ShopSettings) => apiClient.put<ShopSettings>('/shop/settings', s).then((r) => r.data),
};
